import React, { useEffect, useCallback } from 'react';

import { Box } from '@mui/material';
import { useReactFlow } from '@xyflow/react';
import { useTranslate } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';

import { flowchartLogger } from 'src/lib/logger';

import MatchReplaceTestInput from '../../../formInputs/MatchReplaceTestInput/MatchReplaceTestInput';
import CallPropertyInput from '../../components/CallPropertyInput';
import SortableInputsList from '../../components/SortableInputsList';
import NodeControlForm from '../NodeControlForm';

import type { InputColor, NodeControlsProps, SortableInput } from '../../dataProvider/types';

type InputColorList = Record<number, InputColor>;

/**
 * Calculates the colors for the matched rules based on the matched index.
 *
 * @param {number | null} matchedIndex - The index of the matched rule, or null if no match.
 * @param {SortableInput[]} matchRules - An array of sortable input rules.
 * @return {InputColorList} An object representing the colors of the matched rules.
 */
export function getMatchRulesColors(matchedIndex: number | null, matchRules: SortableInput[]): InputColorList {
  if (matchedIndex !== null) {
    flowchartLogger.debug('RouterControls: setting matchRulesColors on match', { matchedIndex });
    return { [matchedIndex]: 'success' };
  } else {
    flowchartLogger.debug('RouterControls: setting error matchRulesColors on no match', { matchedIndex });
    // if no match, set all colors to error
    const newMatchRulesColors: Record<number, InputColor> = {};
    matchRules.forEach((matchRule, index) => {
      newMatchRulesColors[index] = 'error';
    });
    return newMatchRulesColors;
  }
}

export interface RouterType extends Record<string, unknown> {
  matchRules?: SortableInput[];
  matchProperty?: string;
}

interface RouterControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: RouterType;
}

export default function RouterControls({ updateNodeData, data }: RouterControlsProps): React.ReactElement {
  const [matchRules, setMatchRules] = React.useState<SortableInput[]>(
    data.matchRules && data.matchRules.length > 0 ? data.matchRules : [{ id: uuidv4(), value: '' }]
  );
  const [matchRulesColors, setMatchRulesColors] = React.useState<InputColorList>({});
  const [matchProperty, setMatchProperty] = React.useState<string>(data.matchProperty ?? 'called_id_number');
  const reactFlowInstance = useReactFlow();
  const translate = useTranslate();

  const isFormDisabled = useCallback((): boolean => {
    return matchRules.length === 0 || matchRules.some((matchRule) => matchRule.value === '');
  }, [matchRules]);

  useEffect(() => {
    flowchartLogger.debug('RouterControls: updating matchRules', { matchRules });
    updateNodeData({ matchRules, matchProperty });
  }, [matchRules, matchProperty, updateNodeData]);

  const removeEdgesByHandles = useCallback(
    (handles: string[]) => {
      if (handles.length === 0 || reactFlowInstance === undefined) {
        return;
      }
      const edges = reactFlowInstance.getEdges();
      const filteredEdges = edges.filter((edge) => {
        if (edge.sourceHandle === undefined || edge.sourceHandle === null) {
          return true;
        }
        return !handles.includes(edge.sourceHandle);
      });
      if (filteredEdges.length !== edges.length) {
        flowchartLogger.debug('RouterControls: removing edges for handles', { handles });
        reactFlowInstance.setEdges(filteredEdges);
      }
    },
    [reactFlowInstance]
  );

  const handleMatchRuleDelete = useCallback(
    (handleId: string) => {
      flowchartLogger.debug('RouterControls: deleting matchRule handle', { handleId });
      removeEdgesByHandles([handleId]);
    },
    [removeEdgesByHandles]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      updateNodeData({ matchRules, matchProperty });
    },
    [matchRules, matchProperty, updateNodeData]
  );

  const onTestResult = (matchedIndex: number | null): void => {
    setMatchRulesColors(getMatchRulesColors(matchedIndex, matchRules));
    setTimeout(() => {
      setMatchRulesColors({});
    }, 3000);
  };

  return (
    <NodeControlForm onSubmit={handleSubmit} isDisabled={isFormDisabled()} data={data} updateNodeData={updateNodeData}>
      <CallPropertyInput
        callProperty={matchProperty}
        label={translate('callflows.nodes.router.matchProperty')}
        customInputLabel={translate('callflows.nodes.router.customMatchProperty')}
        checkboxLabel={translate('callflows.nodes.router.useCustomMatchProperty')}
        onChange={(matchProperty) => {
          setMatchProperty(matchProperty);
        }}
      />
      <span>{translate('callflows.nodes.router.matchRules')}</span>
      <Box display='flex' flexDirection='column' gap={1}>
        <Box overflow='auto' sx={{ minHeight: 0 }}>
          <SortableInputsList
            inputs={matchRules}
            inputColors={matchRulesColors}
            inputLabel={translate('callflows.nodes.router.matchRule')}
            onChange={setMatchRules}
            onDelete={handleMatchRuleDelete}
          />
        </Box>
        <MatchReplaceTestInput
          label={translate('callflows.components.matchReplaceTestInput.testMatchLabel')}
          matchRules={matchRules.map((matchRule) => ({ match_rule: matchRule.value }))}
          onTestResult={onTestResult}
        />
      </Box>
    </NodeControlForm>
  );
}
